import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    List,
    ListItem,
    ListItemText,
    Stack,
    Grow,
    useTheme,
    Dialog,
    IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { features, industries } from './LOVs';
import EILOGO from 'Assets/Images/favicon.ico';
import CompanyLogo from 'Assets/Images/logo.png';
import { useNavigation } from 'Hooks/navigate';
import CloseIcon from '@mui/icons-material/Close';
import ContactIcon from '@mui/icons-material/ContactMail';
import CustomButton from 'Common/CustomButton';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import CalculateIcon from '@mui/icons-material/Calculate';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShieldIcon from '@mui/icons-material/Shield';
import ElderlyIcon from '@mui/icons-material/Elderly';
import AutomationIcon from '@mui/icons-material/Autorenew';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import GavelIcon from '@mui/icons-material/Gavel';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import SecurityIcon from '@mui/icons-material/Security';

const HeroSection = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #0f172a, #1e293b)',
    color: '#fff',
    padding: theme.spacing(5.5),
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    position: 'relative',
}));

const FeatureCard = styled(Card)(({ theme }) => ({
    height: '100%',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
    },
}));


const LandingPage = () => {
    const { goToContact, goToDashboard, goToSubscription } = useNavigation();
    const [selectedCard, setSelectedCard] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [hoverTimer, setHoverTimer] = useState(null);
    const disablePricing = true; // Set to true to disable pricing buttons

    const handleCardClick = (index) => {
        // Clear any pending hover timer
        if (hoverTimer) {
            clearTimeout(hoverTimer);
            setHoverTimer(null);
        }
        setSelectedCard(index);
        setModalOpen(true);
    };

    const handleCardHoverStart = (index) => {
        // Set a timer to open modal after 2 seconds
        const timer = setTimeout(() => {
            setSelectedCard(index);
            setModalOpen(true);
        }, 2000);
        setHoverTimer(timer);
    };

    const handleCardHoverEnd = () => {
        // Cancel the timer if user stops hovering
        if (hoverTimer) {
            clearTimeout(hoverTimer);
            setHoverTimer(null);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedCard(null);
    };

    // Content for each card - Family and Advisor perspectives
    const getCardContent = (cardIndex, userType) => {
        const content = {
            0: { // Strategic Wealth Planning
                family: "A unified, long-term roadmap that aligns your wealth with your life goals — protecting what you've built and enabling what matters most to future generations.",
                advisor: "Positions you as a trusted architect of the client's full financial picture, expanding scope beyond products to become the central relationship."
            },
            1: { // Cross-Border Wealth Transfer Planning
                family: "Navigate complex multi-jurisdictional rules with confidence, ensuring assets move efficiently across borders while minimizing tax exposure and legal risk.",
                advisor: "Differentiates your practice with specialized expertise that few advisors offer, attracting high-net-worth families with international ties."
            },
            2: { // Tax Optimization Strategies
                family: "Keep more of what you earn through proactive, personalized strategies — reducing tax drag on wealth accumulation and transfers across generations.",
                advisor: "Delivers measurable, tangible value clients can see each year, reinforcing your indispensability and deepening retention."
            },
            3: { // Estate & Legacy Planning
                family: "Ensure your wealth, values, and wishes are preserved and passed on exactly as you intend — protecting your family and honoring your life's work.",
                advisor: "Creates multi-generational client relationships that extend your book of business well beyond the founding client."
            },
            4: { // Retirement & Investment Planning
                family: "Achieve the financial freedom to live on your terms — with a sustainable income strategy that keeps pace with your lifestyle, health needs, and goals.",
                advisor: "Anchors ongoing, recurring advisory relationships with clear milestones, making your value easy for clients to articulate and referrals natural."
            },
            5: { // Business & Succession Planning
                family: "Protect the business you've built and ensure a smooth, tax-efficient transition — whether to family, partners, or a third-party buyer — on your timeline.",
                advisor: "Opens the door to complex, high-value engagements involving legal, tax, and valuation professionals, raising your profile and fee potential."
            },
            6: { // Charitable Giving Strategies
                family: "Amplify the impact of your generosity through structures that maximize charitable outcomes while capturing meaningful tax advantages for your family.",
                advisor: "Deepens client loyalty by connecting to their sense of purpose, and introduces planned giving conversations that strengthen long-term estate mandates."
            },
            7: { // Risk & Insurance Planning
                family: "Safeguard everything you've worked for — protecting your family, income, and assets from life's unexpected disruptions with the right coverage in place.",
                advisor: "Rounds out your holistic offering and protects clients from gaps that could undermine years of careful planning, reinforcing your role as a complete advisor."
            }
        };

        return content[cardIndex]?.[userType] || "Content not available.";
    };

    const enableFeatures = [
        {
            icon: <TrendingUpIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Strategic Wealth Planning",
            desc: "Align long-term goals with actionable strategies."
        },
        {
            icon: <PublicIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Cross-Border Wealth Transfer Planning",
            desc: "Simplify global multi-asset portfolio transitions."
        },
        {
            icon: <CalculateIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Tax Optimization Strategies",
            desc: "Minimize tax burden through strategic planning."
        },
        {
            icon: <ElderlyIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Estate & Legacy Planning",
            desc: "Preserve wealth across generations."
        },
        {
            icon: <AccountBalanceIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Retirement & Investment Planning",
            desc: "Build resilient portfolios for the future."
        },
        {
            icon: <AccountTreeIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Business & Succession Planning",
            desc: "Ensure continuity and growth."
        },
        {
            icon: <FavoriteBorderIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Charitable Giving Strategies",
            desc: "Maximize impact with structured purposeful philanthropy."
        },
        {
            icon: <ShieldIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: "Risk & Insurance Planning",
            desc: "Protect assets and manage liquidity."
        }
    ];

    const platformFeatures = [
        {
            icon: <AutomationIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: features[0].title,
            desc: features[0].description
        },
        {
            icon: <VerifiedUserIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: features[1].title,
            desc: features[1].description
        },
        {
            icon: <SupportAgentIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: features[2].title,
            desc: features[2].description
        },
        {
            icon: <GavelIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: features[3].title,
            desc: features[3].description
        },
        {
            icon: <IntegrationInstructionsIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: features[4].title,
            desc: features[4].description
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 32, color: '#60a5fa' }} />,
            title: features[5].title,
            desc: features[5].description
        }
    ];

    return (
        <Box sx={{
            background: "linear-gradient(to right, #041e42, #78a8e6)",
            minHeight: "100vh",
            color: "white"
        }}>
            <Container maxWidth="lg" sx={{ pt: 5 }}>
                {/* Hero Section */}
                <Grow in timeout={1200}>
                    <Grid container spacing={3} alignItems="center" sx={{ pt: 2 }}>
                        <Grid item xs={12} >
                            <HeroSection>
                                <Box sx={{ position: 'absolute', top: 25, right: 25 }}>
                                    <img src={EILOGO} alt="Hero Art" style={{ width: '70px', height: '70px' }} />
                                </Box>
                                <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', fontSize: { xs: '2rem', md: '2.5rem' } }}>
                                    TAS - Trusted Advisory Service
                                    {/* AI Assisted Wealth Advisory Services */}
                                </Typography>
                                <Typography variant="h5" sx={{ color: '#dbeafe', mb: 5 }}>
                                    Where Expertise Meets Opportunity in Private Wealth
                                    {/* Empowering institutions to deliver smarter, faster, and more secure. */}
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <CustomButton
                                        startIcon={<PersonIcon fontSize='small' />}
                                        color={"primary"}
                                        variant="contained"
                                        onClick={() => goToDashboard()}
                                    >
                                        Register / Sign In
                                    </CustomButton>
                                    <CustomButton
                                        startIcon={<AttachMoneyIcon fontSize='small' />}
                                        variant="text"
                                        onClick={() => goToSubscription("advisor")}
                                        disabled={disablePricing}
                                        sx={{
                                            color: disablePricing ? "#9ca3af" : "#e5e5e5",
                                            borderColor: "#e5e5e5",
                                            backgroundColor: disablePricing ? "#334155" : "#334155",
                                            '&:hover': { backgroundColor: '#1e293b', color: "inherit", borderColor: "#e5e5e5" },
                                        }}
                                    >
                                        View Pricing for Advisors
                                    </CustomButton>
                                    <CustomButton
                                        startIcon={<AttachMoneyIcon fontSize='small' />}
                                        variant="text"
                                        // size="large"
                                        onClick={() => goToSubscription("client")}
                                        disabled={disablePricing}
                                        sx={{
                                            color: disablePricing ? "#9ca3af" : "#e5e5e5",
                                            borderColor: "#e5e5e5",
                                            backgroundColor: disablePricing ? "#334155" : "#334155",
                                            '&:hover': { backgroundColor: '#1e293b', color: "inherit", borderColor: "#e5e5e5" },
                                        }}
                                    >
                                        View Pricing for Family
                                    </CustomButton>
                                </Stack>
                            </HeroSection>
                        </Grid>
                    </Grid>
                </Grow>

                {/* What WealthEI Enable Section */}
                <Grow in timeout={1400}>
                    <Box sx={{
                        my: 6,
                        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                        borderRadius: 3,
                        padding: { xs: 3, md: 5 },
                    }}>
                        <Box sx={{ mb: 5 }}>
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                                    mb: 2,
                                    textAlign: "center"
                                }}
                            >
                                What WealthEI Enables
                            </Typography>
                            <Box
                                sx={{
                                    width: '80px',
                                    height: '3px',
                                    background: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                                    margin: '0 auto 2rem',
                                    borderRadius: 2,
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{
                                    textAlign: 'start',
                                    // maxWidth: '800px',
                                    mx: 'auto',
                                    color: '#cbd5e1',
                                    lineHeight: 1.7,
                                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                                    mb: 4
                                }}
                            >
                                The WealthEI illuminates what your wealth strategy and
                                planning require, while giving you full oversight to identify,
                                manage, and confidently control your relationships with
                                professional advisors.
                                {/* Empowering institutions to deliver smarter, faster, and more secure AI assisted wealth advisory services.
                                Our platform provides a secure collaboration environment for the private wealth ecosystem, connecting advisors, firms, and investment partners to engage, plan, and manage deliverables. */}
                            </Typography>
                            <Typography
                            variant="h6"
                            sx={{
                                textAlign: "start",
                                // maxWidth: '800px',
                                mx: "auto",
                                color: "#cbd5e1",
                                lineHeight: 1.7,
                                fontSize: { xs: "0.95rem", md: "1.1rem" },
                                mb: 4,
                            }}
                            >
                            Augmenting Advisor knowledge to deliver smarter, faster, and
                            more secure wealth advisory services.
                            {/* Empowering institutions to deliver smarter, faster, and more secure AI assisted wealth advisory services.
                                            Our platform provides a secure collaboration environment for the private wealth ecosystem, connecting advisors, firms, and investment partners to engage, plan, and manage deliverables. */}
                            </Typography>
                        </Box>

                        {/* NOTE: An Image will come here, image to be provided by Ali
                        Illustration at Top
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                mb: 5
                            }}
                        >
                            <Box
                                component="img"
                                src={CompanyLogo}
                                alt="Wealth Illustration"
                                sx={{
                                    width: '100%',
                                    maxWidth: 600,
                                    borderRadius: 3,
                                    filter: 'drop-shadow(0px 10px 25px rgba(0,0,0,0.25))',
                                    transition: 'transform 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            />
                        </Box>
 */}
                        {/* Features in 4 columns x 2 rows */}
                        <Grid container spacing={3}>
                            {enableFeatures.map((f, i) => (
                                <Grid item xs={12} sm={6} md={3} key={i}>
                                    <Card
                                        elevation={0}
                                        onClick={() => handleCardClick(i)}
                                        onMouseEnter={() => handleCardHoverStart(i)}
                                        onMouseLeave={handleCardHoverEnd}
                                        sx={{
                                            height: '100%',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            transition: '0.3s',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            '&:hover': {
                                                boxShadow: '0 8px 24px rgba(96, 165, 250, 0.25)',
                                                transform: 'translateY(-4px)',
                                                borderColor: 'rgba(96, 165, 250, 0.3)',
                                            },
                                            '&:hover .info-icon': {
                                                opacity: 1,
                                                transform: 'scale(1)',
                                                animation: 'pulse 1.5s infinite',
                                            }
                                        }}
                                    >
                                        <Box
                                            className="info-icon"
                                            sx={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                opacity: 0,
                                                transform: 'scale(0.8)',
                                                transition: 'all 0.3s ease',
                                                '@keyframes pulse': {
                                                    '0%, 100%': {
                                                        opacity: 0.6,
                                                    },
                                                    '50%': {
                                                        opacity: 1,
                                                    }
                                                }
                                            }}
                                        >
                                            <InfoOutlinedIcon 
                                                sx={{ 
                                                    fontSize: 20, 
                                                    color: '#60a5fa',
                                                    filter: 'drop-shadow(0 0 4px rgba(96, 165, 250, 0.5))'
                                                }} 
                                            />
                                        </Box>
                                        <CardContent sx={{ p: 2.5 }}>
                                            <Stack spacing={1.5} alignItems="center" textAlign="center">
                                                {f.icon}
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fff' }}>
                                                    {f.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#cbd5e1', lineHeight: 1.5 }}
                                                >
                                                    {f.desc}
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                        <Typography
                            variant="h6"
                            sx={{
                                textAlign: 'start',
                                mt: 3,
                                mx: 'auto',
                                color: '#cbd5e1',
                                lineHeight: 1.7,
                                fontSize: { xs: '0.95rem', md: '1.1rem' },
                                mb: 4
                            }}
                        >
                            Our platform provides a secure collaboration environment for the private wealth ecosystem, connecting advisors, firms, and investment partners to engage, plan, and manage deliverables.
                        </Typography>
                    </Box>
                </Grow>

                {/* Platform Capabilities Section */}
                <Grow in timeout={1500}>
                    <Box id="features" sx={{
                        my: 6,
                        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                        borderRadius: 3,
                        padding: { xs: 3, md: 5 },
                    }}>
                        <Box sx={{ textAlign: 'center', mb: 5 }}>
                            <Typography
                                variant="h3"
                                component="h2"
                                gutterBottom
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                                    mb: 2,
                                    color: '#fff',
                                }}
                            >
                                Platform Capabilities
                            </Typography>
                            <Box
                                sx={{
                                    width: '80px',
                                    height: '3px',
                                    background: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                                    margin: '0 auto',
                                    borderRadius: 2,
                                }}
                            />
                        </Box>

                        <Grid container spacing={3}>
                            {platformFeatures.map((f, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            height: '100%',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: 2,
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            transition: '0.3s',
                                            '&:hover': {
                                                boxShadow: '0 8px 24px rgba(96, 165, 250, 0.25)',
                                                transform: 'translateY(-4px)',
                                                borderColor: 'rgba(96, 165, 250, 0.3)',
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.5 }}>
                                            <Stack spacing={1.5} alignItems="center" textAlign="center">
                                                {f.icon}
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fff' }}>
                                                    {f.title}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#cbd5e1', lineHeight: 1.5 }}
                                                >
                                                    {f.desc}
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grow>

                {/* Why Choose Us & Built for Private Wealth Collaboration Section */}
                <Grow in timeout={1500}>
                    <Box sx={{ my: 6 }}>
                        <Grid container spacing={4}>
                            {/* Why Choose Us */}
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                                        color: '#fff',
                                        padding: { xs: 3, md: 4 },
                                        borderRadius: 3,
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        component="h2"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: '#fff'
                                        }}
                                    >
                                        Why Choose Us
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: '60px',
                                            height: '3px',
                                            background: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                                            mb: 3,
                                            borderRadius: 2,
                                        }}
                                    />
                                    <List sx={{ '& .MuiListItem-root': { py: 1 } }}>
                                        <ListItem sx={{ display: 'list-item', listStyleType: 'none', pl: 0 }}>
                                            <Typography variant="body1" sx={{ color: '#e0f2fe', fontSize: '1.05rem', mb: 0.5 }}>
                                                <strong style={{ color: '#fff' }}>AI-Powered Insights</strong> - for smarter decision-making
                                            </Typography>
                                        </ListItem>
                                        <ListItem sx={{ display: 'list-item', listStyleType: 'none', pl: 0 }}>
                                            <Typography variant="body1" sx={{ color: '#e0f2fe', fontSize: '1.05rem', mb: 0.5 }}>
                                                <strong style={{ color: '#fff' }}>Secure Collaboration</strong> - with trusted advisors
                                            </Typography>
                                        </ListItem>
                                        <ListItem sx={{ display: 'list-item', listStyleType: 'none', pl: 0 }}>
                                            <Typography variant="body1" sx={{ color: '#e0f2fe', fontSize: '1.05rem', mb: 0.5 }}>
                                                <strong style={{ color: '#fff' }}>Global Perspective</strong> - for cross-border wealth management
                                            </Typography>
                                        </ListItem>
                                    </List>
                                </Paper>
                            </Grid>

                            {/* Built for Private Wealth Collaboration */}
                            <Grid item xs={12} md={6}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                                        color: '#fff',
                                        padding: { xs: 3, md: 4 },
                                        borderRadius: 3,
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        component="h2"
                                        gutterBottom
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: '#fff'
                                        }}
                                    >
                                        Built for Secure Collaboration
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: '60px',
                                            height: '3px',
                                            background: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                                            mb: 3,
                                            borderRadius: 2,
                                        }}
                                    />
                                    <List sx={{ '& .MuiListItem-root': { py: 1 } }}>
                                        {industries.map((industry, index) => (
                                            <ListItem key={index} sx={{ display: 'list-item', listStyleType: 'none', pl: 0 }}>
                                                <Typography variant="body1" sx={{ color: '#e0f2fe', fontSize: '1.05rem', mb: 0.5 }}>
                                                    {industry}
                                                </Typography>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </Grow>



                <Grow in timeout={2600}>

                    <Grid container>
                        <Grid item xs={12} sx={{ textAlign: "center" }}>
                            <CustomButton
                                startIcon={<ContactIcon fontSize='small' />}
                                variant="text"
                                onClick={() => goToContact()}
                                sx={{
                                    color: "#e5e5e5",
                                    borderColor: "#e5e5e5",
                                    backgroundColor: '#334155',
                                    '&:hover': { backgroundColor: '#1e293b', color: "inherit", borderColor: "#e5e5e5" },
                                }}
                            >
                                Contact Us
                            </CustomButton>
                        </Grid>
                    </Grid>
                </Grow>

                {/* Feature Details Modal */}
                <Dialog
                    open={modalOpen}
                    onClose={handleCloseModal}
                    maxWidth="lg"
                    fullWidth
                    PaperProps={{
                        sx: {
                            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                            borderRadius: 3,
                            border: '1px solid rgba(96, 165, 250, 0.2)',
                        }
                    }}
                >
                    <Box sx={{ position: 'relative', p: { xs: 3, md: 4 } }}>
                        <IconButton
                            onClick={handleCloseModal}
                            sx={{
                                position: 'absolute',
                                right: 12,
                                top: 12,
                                color: '#94a3b8',
                                zIndex: 1,
                                '&:hover': { color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {selectedCard !== null && (
                            <>
                                {/* Modal Header */}
                                <Box sx={{ mb: 4, pr: 5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                                        {enableFeatures[selectedCard].icon}
                                        <Typography variant="h4" fontWeight={700} sx={{ color: '#fff' }}>
                                            {enableFeatures[selectedCard].title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#94a3b8', fontSize: '1rem' }}>
                                        {enableFeatures[selectedCard].desc}
                                    </Typography>
                                </Box>

                                {/* Side-by-side content */}
                                <Grid container spacing={3}>
                                    {/* For Families */}
                                    <Grid item xs={12} md={6}>
                                        <Box
                                            sx={{
                                                background: 'rgba(96, 165, 250, 0.08)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(96, 165, 250, 0.2)',
                                                p: 3,
                                                height: '100%',
                                                transition: '0.3s',
                                                '&:hover': {
                                                    background: 'rgba(96, 165, 250, 0.12)',
                                                    borderColor: 'rgba(96, 165, 250, 0.3)',
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                                                <FamilyRestroomIcon sx={{ fontSize: 28, color: '#60a5fa' }} />
                                                <Typography variant="h6" fontWeight={600} sx={{ color: '#fff' }}>
                                                    Family
                                                </Typography>
                                            </Box>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    color: '#e0f2fe', 
                                                    lineHeight: 1.8,
                                                    fontSize: '0.95rem'
                                                }}
                                            >
                                                {getCardContent(selectedCard, 'family')}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* For Advisors */}
                                    <Grid item xs={12} md={6}>
                                        <Box
                                            sx={{
                                                background: 'rgba(139, 92, 246, 0.08)',
                                                borderRadius: 2,
                                                border: '1px solid rgba(139, 92, 246, 0.2)',
                                                p: 3,
                                                height: '100%',
                                                transition: '0.3s',
                                                '&:hover': {
                                                    background: 'rgba(139, 92, 246, 0.12)',
                                                    borderColor: 'rgba(139, 92, 246, 0.3)',
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                                                <BusinessCenterIcon sx={{ fontSize: 28, color: '#a78bfa' }} />
                                                <Typography variant="h6" fontWeight={600} sx={{ color: '#fff' }}>
                                                    Advisor
                                                </Typography>
                                            </Box>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    color: '#e9d5ff', 
                                                    lineHeight: 1.8,
                                                    fontSize: '0.95rem'
                                                }}
                                            >
                                                {getCardContent(selectedCard, 'advisor')}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </Box>
                </Dialog>

                {/* Footer */}
                <Box sx={{ mt: 4, pb: 2 }}>
                    <Typography variant="body2" align="center">
                        © {new Date().getFullYear()} <a href='https://evolvingintelligence.io/' target='_blank' rel='noopener noreferrer' style={{ color: "inherit", textDecoration: "none" }}>Evolving Intelligence.</a> All rights reserved.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default LandingPage